import { useState, useEffect, useRef, useContext } from "react";
import { useAppAuth } from "../MockAuthProvider";
import { record } from "aws-amplify/analytics";
import { ChatContext } from "../context/ChatContext";
import { LOADING_MESSAGES } from "../data/sampleQuestions";
import { currentConfig, LOCAL_DEV_MODE } from "../config";

export const useChatCoachAI = () => {
  const auth = useAppAuth();
  
  const { messages, setMessages, sessionId } = useContext(ChatContext);
  const [userInput, setUserInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(LOADING_MESSAGES[0]);
  const scrollRef = useRef(null);
  const sessionStartTimeRef = useRef(Date.now());

  // Cycle loadingMessage when loading starts
  useEffect(() => {
    if (!loading) return;
    let idx = 0;
    setLoadingMessage(LOADING_MESSAGES[idx]);
    const interval = setInterval(() => {
      idx = (idx + 1) % LOADING_MESSAGES.length;
      setLoadingMessage(LOADING_MESSAGES[idx]);
    }, 5000);
    return () => clearInterval(interval);
  }, [loading]);

  // Track Unique View and Session Duration (skip in local dev)
  useEffect(() => {
    if (LOCAL_DEV_MODE) {
      console.log('🔧 [LOCAL DEV MODE] Chat analytics disabled for local testing');
      return;
    }
    
    record({
      name: "page_view",
      attributes: { userEmail: auth.user?.profile?.email, page: "ChatCoachAI" },
    });
    sessionStartTimeRef.current = Date.now();
    record({ name: "session_start" });

    const recordSessionEnd = () => {
      const sessionEndTime = Date.now();
      const durationInSeconds = Math.round(
        (sessionEndTime - sessionStartTimeRef.current) / 1000
      );
      record({
        name: "session_end",
        attributes: { duration: durationInSeconds.toString() },
        metrics: { sessionDuration: durationInSeconds },
      });
    };

    const handleBeforeUnload = () => recordSessionEnd();
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      recordSessionEnd();
    };
  }, []);

  // Send message function with conditional authentication
  const handleSend = async (question = null) => {
    const inputText = question || userInput;
    if (!inputText.trim()) return;

    // Record a chat message event (skip in local dev)
    if (!LOCAL_DEV_MODE) {
      record({
        name: "chat_message_sent",
        attributes: {
          page: "ChatCoachAI",
          agentType: "roleplay",
        },
      });
    } else {
      console.log('🔧 [LOCAL DEV MODE] Chat message analytics disabled');
    }

    // Authentication handling - match original working code
    if (!auth || !auth.isAuthenticated) {
      console.error("User is not authenticated");
      return;
    }

    const token = auth.user?.id_token; // Use id_token like original
    if (!token) {
      console.error("Missing access token");
      setMessages([
        ...messages,
        {
          role: "assistant",
          content: "Authentication token is missing. Please sign in again.",
        },
      ]);
      return;
    }


    setUserInput("");
    setLoading(true);

    // Append the new user message to the current messages
    const newMessages = [...messages, { role: "user", content: inputText }];
    setMessages(newMessages);

    // Build a prompt that includes the conversation history
    const conversationHistory = newMessages
      .map(
        (msg) => `${msg.role === "user" ? "User" : "CoachAI"}: ${msg.content}`
      )
      .join("\n");

    try {
      const apiUrl = currentConfig.apiUrl;
      const response = await fetch(`${apiUrl}/coachai`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`, // Always send Bearer token like original
        },
        body: JSON.stringify({
          prompt: conversationHistory,
          sessionId: sessionId || "",
          stream: true,
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.error("401 Unauthorized - API authentication issue");
        }
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data && data.text) {
        setMessages([...newMessages, { role: "assistant", content: data.text }]);
      } else {
        setMessages([...newMessages, { role: "assistant", content: "No response received." }]);
      }
    } catch (error) {
      console.error("Error calling AI:", error);
      setMessages([
        ...newMessages,
        { role: "assistant", content: "Error retrieving AI response." },
      ]);
    } finally {
      setLoading(false);
      setStreaming(false);
    }
  };

  return {
    userInput,
    setUserInput,
    loading,
    streaming,
    loadingMessage,
    handleSend,
    scrollRef,
  };
};
